/**
 * Utility functions for provider streaming
 */
// Removed stream status config imports - using string literals
import { ChatCompletionService } from "@/services/openai/chatCompletionService";

// Constants for utilities
const TOOL_MESSAGE_ID_PREFIX = "tool_msg_";

// Parameter caching for expensive operations
const parameterCache = new Map();
const providerConfigCache = new Map();

// Cache expiry time (5 minutes)
const CACHE_EXPIRY = 5 * 60 * 1000;
const MAX_CACHE_SIZE = 200; // Prevent unlimited growth

// Create service instance
const chatCompletionService = new ChatCompletionService();

/**
 * Safely accumulate streaming function arguments
 * @param {string} current - Current accumulated arguments
 * @param {string} addition - New arguments to add
 * @returns {string} - Safely combined arguments
 */
export function safelyAccumulateArguments(current, addition) {
  if (!addition) return current || "";

  // Simple string concatenation for streaming
  return (current || "") + addition;
}

/**
 * Get provider from model store for a given model
 * @param {string} modelName - The name of the model
 * @returns {string} - The provider ID from model store
 */
export function getProviderFromModelStore(modelName) {
  if (!modelName) return "openai";

  try {
    // Import model store dynamically to avoid circular dependencies
    const { useModelStore } = require("@/store/modelStore");
    const modelStore = useModelStore.getState();

    // First try to get provider from model store's getProviderForModel method
    if (modelStore.getProviderForModel) {
      const provider = modelStore.getProviderForModel(modelName);

      return provider;
    }

    // Fallback to the legacy detection method
    return getProviderForModelLegacy(modelName);
  } catch (error) {
    // Fallback to legacy detection
    return getProviderForModelLegacy(modelName);
  }
}

/**
 * Legacy helper function to determine the correct provider based on model name
 * @param {string} modelName - The name of the model
 * @returns {string} - The provider ID
 */
export function getProviderForModelLegacy(modelName) {
  if (!modelName) return "openai";

  // First, try to use MODEL_PROVIDERS mapping if available
  try {
    const { MODEL_PROVIDERS } = require("@/lib/constants/app");
    if (MODEL_PROVIDERS && MODEL_PROVIDERS[modelName]) {
      return MODEL_PROVIDERS[modelName];
    }
  } catch (error) {
    // Fall through to pattern matching if import fails
  }

  // Check for OpenRouter models first (they have a distinctive format with slash)
  if (modelName.includes("/")) {
    const prefix = modelName.split("/")[0].toLowerCase();

    // Known providers on OpenRouter
    if (
      prefix === "cognitivecomputations" ||
      prefix === "nousresearch" ||
      prefix === "teknium" ||
      prefix === "migtissera"
    ) {
      return "openrouter";
    }
  }

  // Check model name prefixes to determine provider
  if (modelName.includes("deepseek")) return "deepseek";
  if (modelName.includes("gemini")) return "gemini";
  if (modelName.includes("mistral")) return "mistral";
  if (modelName.includes("claude")) return "anthropic";
  if (modelName.includes("qwen")) return "qwen";
  if (modelName.includes("grok")) return "xai";
  if (
    modelName.includes("llama") || // Matches llama3, llama-3.3, etc.
    modelName.includes("mixtral") ||
    modelName.includes("gemma") ||
    modelName.includes("moonshotai") ||
    modelName.includes("kimi")
  )
    return "groq";
  if (modelName.includes("command")) return "cohere";
  if (modelName.includes("sonar") || modelName.includes("r1-1776"))
    return "perplexity";

  // Default to OpenAI for all other models
  return "openai";
}

/**
 * Helper function to determine the correct provider based on model name
 * @param {string} modelName - The name of the model
 * @returns {string} - The provider ID
 */
export function getProviderForModel(modelName) {
  // Use the model store-aware function
  return getProviderFromModelStore(modelName);
}

/**
 * Check if a model is a reasoning model (o-series)
 * @param {string} modelName - The name of the model
 * @returns {boolean} - True if it's a reasoning model
 */
export function isReasoningModel(modelName) {
  if (!modelName) return false;

  // List of known reasoning models
  const reasoningModels = [
    "o1",
    "o1-preview",
    "o1-mini",
    "o3",
    "o3-preview",
    "o3-mini",
  ];

  // Check against the list of known reasoning models
  return reasoningModels.some(
    (model) => modelName === model || modelName.startsWith(`${model}-`)
  );
}

/**
 * Get the reasoning effort parameter from context data or assistant metadata
 * @param {Object} contextData - Context data for the assistant
 * @param {Object} assistant - Assistant data
 * @returns {string|null} - The reasoning effort level or null if not applicable
 */
export function getReasoningEffort(contextData, assistant) {
  // First check directly in context data
  if (contextData?.reasoning_effort) {
    return contextData.reasoning_effort;
  }

  // Then check in assistant metadata
  if (assistant?.metadata?.reasoning_effort) {
    return assistant.metadata.reasoning_effort;
  }

  // Check assistant model and return default reasoning effort if it's a reasoning model
  if (assistant?.model && isReasoningModel(assistant.model)) {
    return "medium"; // Default to medium if not specified
  }

  // Not a reasoning model or no effort specified
  return null;
}

/**
 * Check if string contains a potentially complete JSON object
 * @param {string} str - String to check
 * @returns {boolean} - True if likely a complete JSON object
 */
export function isCompleteJsonObject(str) {
  if (!str) return false;

  // Remove whitespace for easier counting
  const trimmed = str.trim();

  // Basic structure check
  if (!trimmed.startsWith("{") || !trimmed.includes("}")) {
    return false;
  }

  // Count opening and closing braces
  const openBraces = (trimmed.match(/{/g) || []).length;
  const closeBraces = (trimmed.match(/}/g) || []).length;

  // Complete JSON should have matching brace counts
  // and at least one pair of braces
  return openBraces > 0 && openBraces === closeBraces;
}

/**
 * Check if content is in reasoning format
 * @param {string} content - The content to check
 * @returns {boolean} - True if content appears to be reasoning
 */
export function isReasoningContent(content) {
  if (!content || typeof content !== "string") return false;

  try {
    // Check for our custom delimiter markers first
    if (content.includes("----REASONING_START----")) {
      return true;
    }

    // Trim and check first few characters
    const trimmed = content.trim();

    // Check if it starts like a JSON object
    if (
      trimmed.startsWith("{") &&
      (trimmed.includes('"title"') || trimmed.includes('"content"'))
    ) {
      // Try to parse as JSON and check for reasoning structure
      try {
        const parsed = JSON.parse(trimmed);
        // Check for properties that indicate reasoning
        return (
          parsed.title &&
          parsed.content &&
          (parsed.title.includes("Thinking") ||
            parsed.title.includes("Reasoning") ||
            parsed.title.includes("Analysis"))
        );
      } catch (e) {
        // Not valid JSON yet, but might be incomplete
        // Check for patterns that suggest reasoning JSON
        return (
          trimmed.includes('"title":') &&
          trimmed.includes('"content":') &&
          (trimmed.includes("Thinking") ||
            trimmed.includes("Reasoning") ||
            trimmed.includes("Analysis"))
        );
      }
    }

    return false;
  } catch (e) {
    return false;
  }
}

/**
 * Extract reasoning metadata from API response
 * @param {Object} response - API response object
 * @returns {Object|null} - Reasoning metadata or null if not found
 */
export function extractReasoningMetadata(response) {
  try {
    // Look for reasoning tokens in the response
    if (response?.choices?.[0]?.delta?.metadata?.reasoning_token_count) {
      return {
        reasoning_token_count:
          response.choices[0].delta.metadata.reasoning_token_count,
        reasoning_seconds: response.choices[0].delta.metadata.reasoning_seconds,
      };
    }

    // Check for reasoning in message metadata
    if (response?.choices?.[0]?.message?.metadata?.reasoning_token_count) {
      return {
        reasoning_token_count:
          response.choices[0].message.metadata.reasoning_token_count,
        reasoning_seconds:
          response.choices[0].message.metadata.reasoning_seconds,
      };
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Simple stub implementation of processFunctionResult that maintains backwards compatibility.
 * The actual functionality has been integrated directly into streamProviderChat.
 */
export async function processFunctionResult({
  providerName,
  modelName,
  messages,
  functionResult,
  functionName,
  threadId,
  threadStore,
  updateMessageState,
  contextData = {},
  saveToDatabase = null,
  workspace_id,
  tokenCounter,
}) {
  // Return a minimal response
  return {
    messageId: null,
    content: "",
    functionResultMessageId: null,
    functionName,
    isComplete: true,
  };
}

/**
 * Get current file names from the file storage store
 * @returns {Promise<string[]>} - Array of current file names
 */
async function getCurrentFileNames() {
  try {
    const { useFileStorageStore } = await import("@/store/fileStorageStore");
    const fileStorageState = useFileStorageStore.getState();
    const currentFiles = fileStorageState.currentFiles || [];

    // Extract file names, preferring original_name over name
    const fileNames = currentFiles
      .map((file) => file.original_name || file.name)
      .filter(Boolean)
      .filter((name) => name !== "Unknown file" && name !== "Unnamed File");

    return fileNames;
  } catch (error) {
    return [];
  }
}

/**
 * Enhance query with file names for better thread retrieval
 * @param {string} originalQuery - The original query
 * @param {string[]} fileNames - Array of current file names
 * @returns {string} - Enhanced query with file names
 */
function enhanceQueryWithFileNames(originalQuery, fileNames) {
  if (!fileNames || fileNames.length === 0) {
    return originalQuery;
  }

  // Create a file context section to append to the query
  const fileContext = `\n\nThe current files are: ${fileNames.join(", ")}`;

  return originalQuery + fileContext;
}

/**
 * Perform retrieval from a workspace's vector store
 * @param {Object} contextData - Context data containing vector store info
 * @returns {Promise<Object|null>} - The retrieval results object or null
 */
export async function retrieveWorkspaceContext(contextData) {
  const callId = Math.random().toString(36).substr(2, 9);

  try {
    // SIMPLIFIED: Get vector store IDs using the centralized thread store approach
    let workspaceVectorStoreId = null;
    let threadVectorStoreId = null;

    // Priority 1: Get from thread store (single source of truth)
    if (contextData?.thread_id) {
      try {
        const { useThreadStore } = await import("@/store/threadStore");
        const threadStore = useThreadStore.getState();
        threadVectorStoreId = threadStore.getThreadVectorStore(
          contextData.thread_id
        );
      } catch (storeError) {
        console.error("Error getting thread vector store:", storeError);
      }
    }

    // Priority 2: Fallback to contextData for vector store IDs
    if (!threadVectorStoreId) {
      threadVectorStoreId =
        contextData?.vector_store_id || contextData?.thread_vector_store_id;
    }

    // Workspace vector store (separate from thread vector store)
    workspaceVectorStoreId = contextData?.workspace_vector_store_id;

    // Extract query from context data
    const query = contextData?.message_content || "";

    // Check if we have files or workspace context that would benefit from retrieval
    const hasFiles =
      contextData?.has_files === true ||
      (contextData?.file_count && parseInt(contextData.file_count) > 0) ||
      contextData?.storage_files ||
      (contextData?.pdf_file_ids && contextData.pdf_file_ids.length > 0) ||
      (contextData?.file_ids && contextData.file_ids.length > 0) ||
      contextData?.has_pdfs === true ||
      contextData?.has_images === true ||
      (contextData?.vision_image_urls &&
        contextData.vision_image_urls.length > 0);

    const isWorkspaceChat =
      contextData?.from_workspace_chat === true ||
      contextData?.is_workspace_chat === true;

    // Run retrieval if we have files OR if this is a workspace chat (user might be asking about existing workspace files)
    const shouldRunRetrieval = hasFiles || isWorkspaceChat;

    // Skip retrieval if conditions aren't met
    if (
      !shouldRunRetrieval ||
      (!workspaceVectorStoreId && !threadVectorStoreId) ||
      !query
    ) {
      return null;
    }

    // Perform vector searches - we'll collect results from both stores if available
    const allResults = [];

    // Get current file names for thread retrieval enhancement
    const currentFileNames = await getCurrentFileNames();

    // Search in thread vector store if available (this includes pendingVectorStoreId)
    if (threadVectorStoreId) {
      try {
        // Enhance query with current file names for thread retrieval
        const enhancedQuery = enhanceQueryWithFileNames(
          query,
          currentFileNames
        );

        const threadSearchResults =
          await chatCompletionService.searchVectorStore(
            threadVectorStoreId,
            enhancedQuery, // Use enhanced query for thread search
            10
          );

        if (threadSearchResults?.data?.length > 0) {
          // Add source metadata to distinguish results
          const threadResults = threadSearchResults.data.map((result) => ({
            ...result,
            metadata: {
              ...result.metadata,
              source_type: "thread",
              vector_store_id: threadVectorStoreId,
              enhanced_with_file_names: currentFileNames.length > 0,
              file_names_used: currentFileNames,
            },
          }));
          allResults.push(...threadResults);
        }
      } catch (threadSearchError) {
        // Continue with workspace search even if thread search fails
      }
    }

    // Search in workspace vector store if available, different from thread store, AND this is actually a workspace chat
    if (
      workspaceVectorStoreId &&
      workspaceVectorStoreId !== threadVectorStoreId &&
      isWorkspaceChat
    ) {
      try {
        // Use original query for workspace search (no file name enhancement)
        const workspaceSearchResults =
          await chatCompletionService.searchVectorStore(
            workspaceVectorStoreId,
            query, // Use original query for workspace search
            10
          );

        if (workspaceSearchResults?.data?.length > 0) {
          // Add source metadata to distinguish results
          const workspaceResults = workspaceSearchResults.data.map(
            (result) => ({
              ...result,
              metadata: {
                ...result.metadata,
                source_type: "workspace",
                vector_store_id: workspaceVectorStoreId,
                enhanced_with_file_names: false, // Workspace search not enhanced
              },
            })
          );
          allResults.push(...workspaceResults);
        }
      } catch (workspaceSearchError) {
        // Continue with whatever results we have
      }
    }

    // If we have results from either search, format them
    if (allResults.length > 0) {
      // Sort results by score (highest first) if scores are available
      const sortedResults = allResults.sort((a, b) => {
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        return scoreB - scoreA;
      });

      // Create a combined results object that mimics the original format
      const combinedSearchResults = {
        data: sortedResults,
        query: query,
        enhanced_query_used: currentFileNames.length > 0,
        file_names_included: currentFileNames,
        vector_store_ids: {
          thread: threadVectorStoreId,
          workspace: workspaceVectorStoreId,
        },
      };

      const formattedResults = formatRetrievalResults(combinedSearchResults);

      return formattedResults;
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Format vector store search results for use in the model prompt
 * @param {Object} searchResults - Results from vector store search
 * @returns {Object} Formatted results
 */
export function formatRetrievalResults(searchResults) {
  if (!searchResults?.data || searchResults.data.length === 0) {
    return null;
  }

  // Map search results to a more structured format
  const formattedResults = searchResults.data.map((result, index) => {
    return {
      id: result.id || `result-${index}`,
      score: result.score || 0,
      content: result.text || result.content || "",
      metadata: {
        file_name:
          result.metadata?.file_name || result.filename || "Unknown file",
        file_type: result.metadata?.file_type || result.file_type || "",
        file_id: result.metadata?.file_id || result.file_id || "",
        source: result.metadata?.source || "vector_store",
        source_type: result.metadata?.source_type || "unknown",
        vector_store_id: result.metadata?.vector_store_id || "",
        enhanced_with_file_names:
          result.metadata?.enhanced_with_file_names || false,
        file_names_used: result.metadata?.file_names_used || [],
      },
    };
  });

  return {
    type: "vector_store_search",
    count: formattedResults.length,
    results: formattedResults,
    query: searchResults.query || "",
    enhanced_query_used: searchResults.enhanced_query_used || false,
    file_names_included: searchResults.file_names_included || [],
    vector_store_ids: searchResults.vector_store_ids || {},
  };
}

/**
 * Format retrieval results into XML structure for model consumption
 * @param {Array} results - Array of retrieval results
 * @returns {string} XML formatted results string
 */
export function formatResultsToXML(results) {
  if (!results || results.length === 0) {
    return "";
  }

  let formattedResults = "";

  for (const result of results) {
    const fileId = result.metadata?.file_id || result.file_id || "unknown";
    const fileName =
      result.metadata?.file_name ||
      result.filename ||
      result.file_name ||
      "Unknown file";

    // Add source attribution if available
    const sourceType = result.metadata?.source_type;
    const sourceInfo = sourceType ? ` source="${sourceType}"` : "";

    let formattedResult = `<result file_id='${fileId}' file_name='${fileName}'${sourceInfo}>`;

    // Handle different content formats
    if (Array.isArray(result.content)) {
      // Content is an array of parts
      for (const part of result.content) {
        const text = part.text || part || "";
        if (text) {
          formattedResult += `<content>${text}</content>`;
        }
      }
    } else if (typeof result.content === "string") {
      // Content is a string
      if (result.content) {
        formattedResult += `<content>${result.content}</content>`;
      }
    } else if (result.content?.text) {
      // Content has a text property
      formattedResult += `<content>${result.content.text}</content>`;
    } else if (result.text) {
      // Fallback to direct text property
      formattedResult += `<content>${result.text}</content>`;
    }

    formattedResults += formattedResult + "</result>";
  }

  return `<sources>${formattedResults}</sources>`;
}

// ===== ID UTILITIES =====

/**
 * Generate a unique UUID v4
 * @returns {string} A UUID string
 */
export function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate a tool message ID
 * @param {string} toolCallId - The associated tool call ID
 * @returns {string} A tool message ID
 */
export function generateToolMessageId(toolCallId) {
  return `${TOOL_MESSAGE_ID_PREFIX}${toolCallId}_${Date.now()}`;
}

// ===== PARAMETER UTILITIES =====

/**
 * Clean up expired cache entries
 */
function cleanupExpiredCache() {
  const now = Date.now();

  // Clean parameter cache
  for (const [key, value] of parameterCache.entries()) {
    if (now - value.timestamp > CACHE_EXPIRY) {
      parameterCache.delete(key);
    }
  }

  // If still too large, remove oldest entries
  if (parameterCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(parameterCache.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp
    );

    const toRemove = entries.slice(0, parameterCache.size - MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => parameterCache.delete(key));
  }
}

// Periodic cleanup every 5 minutes
setInterval(cleanupExpiredCache, CACHE_EXPIRY);

/**
 * Get cached or create new parameters
 * @param {string} cacheKey - Cache key
 * @param {Function} factory - Function to create parameters if not cached
 * @returns {Object} Cached or new parameters
 */
function getCachedParameters(cacheKey, factory) {
  const cached = parameterCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return cached.data;
  }

  const data = factory();
  parameterCache.set(cacheKey, { data, timestamp: Date.now() });

  // Trigger cleanup if cache is getting large
  if (parameterCache.size > MAX_CACHE_SIZE * 1.2) {
    cleanupExpiredCache();
  }

  return data;
}

/**
 * Get default chat parameters for streaming
 * @param {string} modelName - The model name
 * @param {Array} messages - The messages array
 * @param {string} providerName - The provider name
 * @param {Object} contextData - Context data containing optional parameters
 * @returns {Object} - Default chat parameters
 */
export function getDefaultChatParams(
  modelName,
  messages,
  providerName,
  contextData = {}
) {
  // Create cache key for parameters
  const cacheKey = `default-${modelName}-${providerName}-${messages.length}`;

  return getCachedParameters(cacheKey, () => {
    const params = {
      model: modelName,
      messages: messages,
      stream: true,
      temperature: contextData.temperature || 0.2,
      max_tokens: contextData.max_tokens || 16000,
      parallel_tool_calls: contextData.parallel_tool_calls !== false, // Default to true unless explicitly disabled
    };

    // Structured output is now handled individually by canvas tools
    return params;
  });
}

/**
 * Get provider-specific initialization options with caching
 * @param {string} providerName - The provider name
 * @returns {Object} - Provider-specific options
 */
export function getProviderInitOptions(providerName) {
  return getCachedParameters(`provider-init-${providerName}`, () => {
    const options = {};

    // Add dangerouslyAllowBrowser for Anthropic and Cohere providers
    if (providerName === "anthropic" || providerName === "cohere") {
      options.dangerouslyAllowBrowser = true;
    }

    return options;
  });
}

/**
 * Remove unsupported parameters for specific models
 * @param {Object} params - The parameters object
 * @param {string} modelName - The model name
 * @param {string} providerName - The provider name
 * @returns {Object} - Cleaned parameters
 */
export function removeUnsupportedParams(params, modelName, providerName) {
  const cleanedParams = { ...params };

  // Remove presence_penalty for specific Grok models
  if (modelName === "grok-3-mini" || modelName === "grok-3-mini-fast") {
    delete cleanedParams.presence_penalty;

    // Also remove frequency_penalty which might cause issues
    if (cleanedParams.frequency_penalty !== undefined) {
      delete cleanedParams.frequency_penalty;
    }
  }

  // Remove tools for models that don't support them
  const modelsWithoutToolSupport = ["command", "command-light"];

  if (modelsWithoutToolSupport.includes(modelName)) {
    delete cleanedParams.tools;
    delete cleanedParams.tool_choice;
    delete cleanedParams.parallel_tool_calls;
  }

  // Remove tools for OpenRouter models that don't support them
  if (providerName === "openrouter") {
    const nonToolModels = ["command-light", "command-r-light"];
    if (nonToolModels.includes(modelName)) {
      delete cleanedParams.tools;
      delete cleanedParams.tool_choice;
      delete cleanedParams.parallel_tool_calls;
    }
  }

  // Remove structured output for providers/models that don't support it
  if (cleanedParams.response_format && providerName !== "openai") {
    delete cleanedParams.response_format;
  }

  // Only keep structured output for specific OpenAI models
  if (cleanedParams.response_format && providerName === "openai") {
    const supportedModels = [
      "gpt-4o-mini",
      "gpt-4o-mini-2024-07-18",
      "gpt-4o-2024-08-06",
      "gpt-4o",
      "gpt-4o-2024-11-20",
    ];

    if (!supportedModels.some((model) => modelName.includes(model))) {
      delete cleanedParams.response_format;
    }
  }

  return cleanedParams;
}

/**
 * Add reasoning effort parameter for OpenAI reasoning models
 * @param {Object} params - The parameters object
 * @param {string} modelName - The model name
 * @param {string} providerName - The provider name
 * @param {Object} contextData - Context data
 * @param {Object} assistant - Assistant data
 * @returns {Object} - Parameters with reasoning effort added if applicable
 */
export async function addReasoningEffortIfNeeded(
  params,
  modelName,
  providerName,
  contextData,
  assistant
) {
  if (providerName !== "openai") {
    return params;
  }

  try {
    // Check if this is a reasoning model
    const isReasoning = isReasoningModel(modelName);

    if (isReasoning) {
      // Get reasoning effort from context data, assistant metadata, or default
      const reasoningEffort = getReasoningEffort(contextData, assistant);

      if (reasoningEffort) {
        params.reasoning_effort = reasoningEffort;
      }
    }
  } catch (error) {
    console.error("Error adding reasoning effort:", error);
  }

  return params;
}

/**
 * Configure tool parameters for a model (Backend Compatible)
 * @param {Object} params - The parameters object
 * @param {Array} tools - The tools array
 * @param {string} modelName - The model name
 * @param {string} providerName - The provider name
 * @param {Object} contextData - Context data
 * @param {Object} assistant - Assistant data (optional, for ensuring all tools are included)
 * @returns {Object} - Parameters with tools configured
 */
export async function configureToolParams(
  params,
  tools,
  modelName,
  providerName,
  contextData,
  assistant = null
) {
  // Command Light doesn't support tools
  if (modelName === "command-light") {
    return params;
  }

  const updatedParams = { ...params };

  // Start with provided tools, but ensure we include all assistant tools
  let finalTools = [];

  // CRITICAL FIX: Always start with assistant tools if available
  if (
    assistant?.tools &&
    Array.isArray(assistant.tools) &&
    assistant.tools.length > 0
  ) {
    try {
      // Import convertAssistantTools dynamically to avoid circular dependencies
      const { convertAssistantTools } = await import("./providerCompatibility");
      const convertedAssistantTools = convertAssistantTools(
        assistant.tools.filter(Boolean)
      );
      finalTools = [...convertedAssistantTools];
    } catch (conversionError) {
      console.error("Error converting assistant tools:", conversionError);
      // Fallback to provided tools
      finalTools = tools ? [...tools] : [];
    }
  } else {
    // Use provided tools if no assistant tools
    finalTools = tools ? [...tools] : [];
  }

  // Add any additional tools that were provided but not in assistant tools
  if (tools && Array.isArray(tools) && tools.length > 0) {
    const existingToolNames = new Set(
      finalTools.map((t) => t.function?.name || t.type)
    );

    const additionalTools = tools.filter((tool) => {
      const toolName = tool.function?.name || tool.type;
      return !existingToolNames.has(toolName);
    });

    if (additionalTools.length > 0) {
      finalTools.push(...additionalTools);
    }
  }

  // Only add canvas tools when explicitly requested or canvas mode is enabled
  const shouldIncludeCanvasTools =
    contextData?.isCanvasModeEnabled === true ||
    contextData?.canvasModePreference === true ||
    contextData?.forceCanvasCreate === true;

  console.log("🎨 [Tool Config] Canvas tools check:", {
    shouldIncludeCanvasTools,
    isCanvasModeEnabled: contextData?.isCanvasModeEnabled,
    canvasModePreference: contextData?.canvasModePreference,
    forceCanvasCreate: contextData?.forceCanvasCreate,
  });

  if (shouldIncludeCanvasTools) {
    try {
      // Check if canvas tools are already present
      const hasCanvasCreate = finalTools.some(
        (tool) => tool.function?.name === "canvas_create"
      );
      const hasCanvasEdit = finalTools.some(
        (tool) => tool.function?.name === "canvas_edit"
      );

      // Add canvas tools if they're not already present
      if (!hasCanvasCreate) {
        finalTools.push({
          type: "function",
          function: {
            name: "canvas_create",
            description:
              "Create new canvas content with intelligent generation",
            parameters: {
              type: "object",
              properties: {
                content: {
                  type: "string",
                  description:
                    "Direct content to use (optional if query provided)",
                },
                query: {
                  type: "string",
                  description:
                    "User query for content generation (use this for AI-generated content)",
                },
                name: {
                  type: "string",
                  description: "Canvas name (auto-generated if not provided)",
                },
                type: {
                  type: "string",
                  enum: ["text", "code", "markdown", "html", "spreadsheet"],
                  default: "text",
                  description: "Content type",
                },
                language: {
                  type: "string",
                  default: "javascript",
                  description: "Programming language for code content",
                },
                version: {
                  type: "number",
                  default: 1,
                  description: "Canvas version",
                },
                useStreaming: {
                  type: "boolean",
                  default: true,
                  description: "Use streaming for content generation",
                },
                sourceMessageId: {
                  type: "string",
                  description:
                    "ID of the message that triggered this canvas creation",
                },
              },
              required: [],
              additionalProperties: false,
            },
          },
        });
      }

      if (!hasCanvasEdit) {
        finalTools.push({
          type: "function",
          function: {
            name: "canvas_edit",
            description:
              "Edit existing canvas content based on user instructions",
            parameters: {
              type: "object",
              properties: {
                editQuery: {
                  type: "string",
                  description: "User query describing the desired changes",
                },
                editType: {
                  type: "string",
                  enum: [
                    "add",
                    "modify",
                    "remove",
                    "restructure",
                    "fix",
                    "refactor",
                  ],
                  default: "modify",
                  description: "Type of edit to perform",
                },
                targetSection: {
                  type: "string",
                  description:
                    "Specific section or element to target (optional)",
                },
                useStreaming: {
                  type: "boolean",
                  default: true,
                  description: "Use streaming for intelligent edits",
                },
                sourceMessageId: {
                  type: "string",
                  description:
                    "ID of the message that triggered this canvas edit",
                },
              },
              required: ["editQuery"],
              additionalProperties: false,
            },
          },
        });
      }
    } catch (error) {
      console.error("Error adding canvas tools:", error);
    }
  } else {
    console.log(
      "🎨 [Tool Config] Skipping canvas tools - not in canvas mode and not explicitly requested"
    );
  }

  // Conditionally add web_search and image_gen tools
  // CRITICAL: Only include web_search when explicitly forced to prevent automatic usage
  try {
    // Import compatibility mode check
    const { shouldUseCompatibilityMode } = await import(
      "@/lib/constants/webSearchCompatibility"
    );

    // Only add web_search tool if it's explicitly forced (manually selected by user)
    const shouldIncludeWebSearch = contextData?.forceWebSearchTool === true;

    // Check if compatibility mode should be used (removes tool for compatibility mode models)
    const useCompatibilityMode = shouldUseCompatibilityMode(
      modelName,
      contextData
    );

    if (shouldIncludeWebSearch && !useCompatibilityMode) {
      const hasWebSearch = finalTools.some(
        (tool) => tool.function?.name === "web_search"
      );

      if (!hasWebSearch) {
        finalTools.push({
          type: "function",
          function: {
            name: "web_search",
            description:
              "Search the web for real-time information and return structured data array with sources and citations.",
            parameters: {
              type: "object",
              properties: {
                query: {
                  type: "string",
                  description: "The search query",
                },
                limit: {
                  type: "number",
                  description:
                    "Maximum number of results to return (default: 10)",
                  default: 10,
                },
                searchContextSize: {
                  type: "string",
                  description:
                    "Search context size - affects cost and quality (default: medium)",
                  enum: ["low", "medium", "high"],
                  default: "medium",
                },
                userLocation: {
                  type: "object",
                  description:
                    "User location for geographically relevant results",
                  properties: {
                    country: {
                      type: "string",
                      description: "Two-letter ISO country code (e.g., 'US')",
                    },
                    city: {
                      type: "string",
                      description: "City name (e.g., 'New York')",
                    },
                    region: {
                      type: "string",
                      description: "Region/state name (e.g., 'California')",
                    },
                    timezone: {
                      type: "string",
                      description: "IANA timezone (e.g., 'America/New_York')",
                    },
                  },
                },
              },
              required: ["query"],
            },
          },
        });
      }
    } else {
      // Remove web_search if it exists but shouldn't be included
      // OR if compatibility mode is being used (tool will be executed separately)
      finalTools = finalTools.filter(
        (tool) => tool.function?.name !== "web_search"
      );
      if (useCompatibilityMode) {
        console.log(
          "[Tool Config] Excluding web_search tool - using compatibility mode for",
          modelName
        );
      } else {
        console.log(
          "[Tool Config] Excluding web_search tool - not explicitly requested"
        );
      }
    }

    // Only add image_gen tool if explicitly forced (manually selected by user)
    const shouldIncludeImageGen = contextData?.forceImageGenTool === true;

    if (shouldIncludeImageGen) {
      const hasImageGen = finalTools.some(
        (tool) => tool.function?.name === "image_gen"
      );

      if (!hasImageGen) {
        finalTools.push({
          type: "function",
          function: {
            name: "image_gen",
            description:
              "Generate images using Qwen image generation engine. Can create detailed, high-quality images from text descriptions.",
            parameters: {
              type: "object",
              properties: {
                prompt: {
                  type: "string",
                  description:
                    "A detailed description of the image to generate. Be specific about style, composition, colors, and mood.",
                },
                name: {
                  type: "string",
                  description: "Name or title for the generated image",
                },
                size: {
                  type: "string",
                  enum: ["1024x1024", "1024x1536", "1536x1024"],
                  description: "Image dimensions",
                  default: "1024x1024",
                },
                quality: {
                  type: "string",
                  enum: ["low", "medium", "high"],
                  description: "Image quality level",
                  default: "medium",
                },
              },
              required: ["prompt", "name"],
            },
          },
        });
      }
    } else {
      // Remove image_gen if it exists but shouldn't be included
      finalTools = finalTools.filter(
        (tool) => tool.function?.name !== "image_gen"
      );
      console.log(
        "[Tool Config] Excluding image_gen tool - not explicitly requested"
      );
    }
  } catch (error) {
    console.error("Error configuring tool parameters:", error);
  }

  // Add tools
  updatedParams.tools = finalTools;

  // Get tool_choice from context data or default to "none" for strict tool control
  // If compatibility mode is active, always start with "none"
  let toolChoice =
    contextData?.webSearchCompatibilityMode === true || contextData?.clearToolChoice === true
      ? "none"
      : contextData.tool_choice || "none";

  // Handle forced image edit tool choice
  if (contextData?.forceImageEditTool === true) {
    toolChoice = {
      type: "function",
      function: { name: "image_edit" },
    };
  }

  // Handle forced canvas create tool choice
  if (contextData?.forceCanvasCreate === true) {
    toolChoice = {
      type: "function",
      function: { name: "canvas_create" },
    };
  }

  // Handle forced web search tool choice
  // Skip if compatibility mode is being used (tool already executed and results added to context)
  if (contextData?.forceWebSearchTool === true) {
    // Check if compatibility mode flag is already set (from streamProviderChat)
    if (contextData?.webSearchCompatibilityMode === true || contextData?.clearToolChoice === true) {
      // Compatibility mode: don't set tool_choice since tool is not in tools array
      toolChoice = "none";
      console.log(
        "[Tool Config] Skipping web_search tool_choice - compatibility mode active"
      );
    } else {
      // Check if compatibility mode should be used
      try {
        const { shouldUseCompatibilityMode } = await import(
          "@/lib/constants/webSearchCompatibility"
        );
        const useCompatibilityMode = shouldUseCompatibilityMode(
          modelName,
          contextData
        );

        if (!useCompatibilityMode) {
          toolChoice = {
            type: "function",
            function: { name: "web_search" },
          };
        } else {
          // Compatibility mode: don't set tool_choice since tool is not in tools array
          toolChoice = "none";
          console.log(
            "[Tool Config] Skipping web_search tool_choice - compatibility mode detected"
          );
        }
      } catch (error) {
        console.error("Error checking compatibility mode:", error);
        // Fallback: set tool choice if import fails
        toolChoice = {
          type: "function",
          function: { name: "web_search" },
        };
      }
    }
  }

  // Handle forced image generation tool choice
  if (contextData?.forceImageGenTool === true) {
    console.log(
      "🛠️ [Tool Configuration] Force image generation tool activated:",
      {
        toolChoice: "image_gen",
        contextData: {
          forceImageGenTool: contextData.forceImageGenTool,
          hasMessages: !!params.messages,
          messageCount: params.messages?.length || 0,
        },
        timestamp: new Date().toISOString(),
      }
    );
    toolChoice = {
      type: "function",
      function: { name: "image_gen" },
    };
  }

  // Handle canvas tool choice when in canvas mode (legacy support)
  if (contextData?.isCanvasModeEnabled && contextData?.forceCanvasCreate) {
    toolChoice = {
      type: "function",
      function: { name: "canvas_create" },
    };
  }

  // Only add tool_choice if not a Cohere model (backend handles this now)
  if (providerName !== "cohere") {
    // Final safeguard: delete tool_choice entirely if compatibility mode is active
    // (Some backends may not accept "none" and will still try to use it)
    if (contextData?.webSearchCompatibilityMode === true || contextData?.clearToolChoice === true) {
      delete updatedParams.tool_choice;
      console.log(
        "[Tool Config] Final safeguard: Deleting tool_choice for compatibility mode"
      );
    } else {
      updatedParams.tool_choice = toolChoice;
    }
  }

  // Explicitly set parallel_tool_calls (default to true unless disabled)
  updatedParams.parallel_tool_calls =
    contextData.parallel_tool_calls !== false;

  // Enhanced logging for backend debugging

  // Validate that we don't have duplicate tools
  const toolNames = finalTools
    .map((t) => t.function?.name || t.type)
    .filter(Boolean);
  const uniqueToolNames = [...new Set(toolNames)];

  if (toolNames.length !== uniqueToolNames.length) {
    // Remove duplicates, keeping the first occurrence
    const seenNames = new Set();
    finalTools = finalTools.filter((tool) => {
      const toolName = tool.function?.name || tool.type;
      if (seenNames.has(toolName)) {
        return false;
      }
      seenNames.add(toolName);
      return true;
    });

    updatedParams.tools = finalTools;
  }

  return updatedParams;
}

/**
 * Clean parameters for follow-up requests
 * @param {Object} params - The parameters object
 * @param {string} modelName - The model name
 * @param {string} providerName - The provider name
 * @returns {Object} - Cleaned follow-up parameters
 */
export function cleanFollowUpParams(params, modelName, providerName) {
  const cleanedParams = { ...params };

  // Ensure Command Light doesn't get any tool parameters
  if (modelName === "command-light") {
    delete cleanedParams.tools;
    delete cleanedParams.tool_choice;
    delete cleanedParams.parallel_tool_calls;
    delete cleanedParams.response_format;
  }

  // For providers known to have issues with repeated tool calls in follow-up responses
  if (providerName === "deepseek" || providerName === "anthropic") {
    // Remove tools from follow-up parameters
    delete cleanedParams.tools;
    delete cleanedParams.tool_choice;
    delete cleanedParams.parallel_tool_calls;
    delete cleanedParams.response_format;
  }

  return cleanedParams;
}

/**
 * Helper function to parse boolean or string "true"/"false" values
 * @param {any} value - The value to parse
 * @returns {boolean} - The parsed boolean value
 */
export function getBooleanValue(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value === "true";
  return !!value; // Coerce to boolean for any other type
}

// ===== PDF PROCESSING UTILITIES =====

/**
 * Process PDF files from context data for OpenAI provider
 * @param {Object} contextData - Context data containing file information
 * @returns {Object} - Processed PDF data with file IDs and file objects
 */
export function processPdfFiles(contextData) {
  const hasPdfFiles =
    contextData.has_pdfs === true ||
    contextData.has_pdfs === "true" ||
    (contextData.file_ids && contextData.file_ids.length > 0) ||
    (contextData.storage_files &&
      typeof contextData.storage_files === "string" &&
      JSON.parse(contextData.storage_files).some(
        (file) =>
          file.type?.includes("pdf") ||
          file.type?.includes("application/pdf") ||
          file.name?.toLowerCase().endsWith(".pdf")
      ));

  let pdfFileIds = [];
  let pdfFiles = [];

  if (
    contextData.storage_files &&
    typeof contextData.storage_files === "string"
  ) {
    try {
      const storageFiles = JSON.parse(contextData.storage_files);
      if (Array.isArray(storageFiles)) {
        pdfFiles = storageFiles
          .filter(
            (file) =>
              file.type?.includes("pdf") ||
              file.type?.includes("application/pdf") ||
              file.name?.toLowerCase().endsWith(".pdf")
          )
          .map((file) => ({
            file_id: file.file_id || file.openai_file_id,
            filename: file.name || "document.pdf",
            type: file.type || "application/pdf",
          }));

        pdfFileIds = pdfFiles
          .filter((file) => file.file_id)
          .map((file) => file.file_id);
      }
    } catch (error) {
      console.error("Error processing PDF files:", error);
    }
  }

  if (Array.isArray(contextData.file_ids)) {
    pdfFileIds = [...pdfFileIds, ...contextData.file_ids];
  }

  return {
    hasPdfFiles,
    pdfFileIds,
    pdfFiles,
  };
}

/**
 * Check if vision formatting is needed for the provider
 * @param {string} providerName - The provider name
 * @param {Object} contextData - Context data
 * @returns {boolean} - Whether vision formatting is needed
 */
export function needsVisionFormatting(providerName, contextData) {
  // If universal vision processing has been applied, skip provider-specific formatting
  if (contextData?.has_vision_analysis === true) {
    return false;
  }

  // Only apply OpenAI-specific vision formatting if universal vision hasn't been processed
  return (
    providerName === "openai" &&
    (contextData.has_vision_enabled === true ||
      contextData.has_images === true ||
      contextData.vision_image_urls?.length > 0)
  );
}

/**
 * Check if messages need vision formatting
 * @param {Array} messages - Array of messages
 * @returns {boolean} - Whether messages need vision formatting
 */
export function messagesNeedVisionFormatting(messages) {
  // Check if any message already has vision analysis applied
  const hasVisionAnalysis = messages.some(
    (msg) =>
      msg.metadata?.has_vision_analysis === true ||
      (typeof msg.content === "string" && msg.content.includes("<image>"))
  );

  if (hasVisionAnalysis) {
    return false;
  }

  for (const msg of messages) {
    if (
      msg.role === "user" &&
      typeof msg.content === "string" &&
      !Array.isArray(msg.content)
    ) {
      return true;
    }
  }
  return false;
}
